import React, { useState, useMemo } from 'react';
import './MusicLibrary.css';

const NEW_DAYS = 14;
const SORTS = [['new', 'Newest'], ['used', 'Most used'], ['az', 'A–Z']];
const fmt = s => (s ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}` : '');

// Studio music picker: multi-column grid, search, sort (Newest default), NEW badge, usage count.
// Track shape from GET /api/music: { filename, label, duration, addedAt, usage }.
export default function MusicLibrary({ tracks, selected, missing, localFilenames, uploading, onSelect, onDelete, onUpload, fuzzyMatch }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('new');
  const query = q.trim();

  const list = useMemo(() => {
    let l = query
      ? tracks.map(t => ({ t, score: fuzzyMatch(query, t.label || t.filename || '') })).filter(x => x.score >= 0).map(x => x.t)
      : [...tracks];
    const label = t => t.label || t.filename || '';
    const added = t => (t.addedAt ? Date.parse(t.addedAt) || 0 : 0);
    if (sort === 'new') l.sort((a, b) => added(b) - added(a) || label(a).localeCompare(label(b)));
    else if (sort === 'used') l.sort((a, b) => (b.usage || 0) - (a.usage || 0) || label(a).localeCompare(label(b)));
    else l.sort((a, b) => label(a).localeCompare(label(b)));
    return l;
  }, [tracks, query, sort, fuzzyMatch]);

  const isNew = t => t.addedAt && Date.now() - Date.parse(t.addedAt) < NEW_DAYS * 864e5;

  return (
    <>
      <div className="ml-top">
        <input className="ml-search" type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Search tracks…" />
        <span className="ml-seg">
          {SORTS.map(([k, l]) => <button key={k} className={sort === k ? 'on' : ''} onClick={() => setSort(k)}>{l}</button>)}
        </span>
        <label className="upload-music-btn" style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
          <input type="file" accept="audio/*" multiple onChange={onUpload} hidden disabled={uploading} />
          {uploading ? 'Uploading…' : '+ Add tracks'}
        </label>
        <span className="ml-count">{query ? `${list.length} result${list.length !== 1 ? 's' : ''}` : `${tracks.length} track${tracks.length !== 1 ? 's' : ''}`}</span>
      </div>
      <div className="ml-grid">
        {list.length === 0 && <div className="music-empty-mood">{query ? 'No matching tracks' : 'No tracks — add one'}</div>}
        {list.map(t => {
          const on = selected === t.filename;
          return (
            <div key={t.filename} className={`ml-tr ${on ? 'on' : ''} ${missing && on ? 'missing' : ''}`}
              onClick={() => onSelect(on ? 'none' : t.filename)} title={t.label}>
              <span className="ml-nm">{on ? '✓ ' : ''}{t.label}</span>
              {isNew(t) && <span className="ml-new">NEW</span>}
              {t.duration ? <span className="ml-u">{fmt(t.duration)}</span> : null}
              {localFilenames.has(t.filename) && <span className="ml-local">●</span>}
              <span className="ml-u">{t.usage ? `${t.usage}×` : 'unused'}</span>
              <button className="delete-btn" onClick={e => { e.stopPropagation(); onDelete(t.filename); }}>✕</button>
            </div>
          );
        })}
      </div>
    </>
  );
}
